<template>
  <!-- Add a technical security measure to a system, or answer whether one is in place (US 9370) -->
  <V2SidePanel ref="panel"
               :kind="$t('system_security.measure_kind')"
               :title="link ? link.name : $t('system_security.add_measure')"
               :intro="link ? $t('system_security.answer_intro', { name: system.name }) : $t('system_security.add_intro', { name: system.name })"
               :busy="saving"
               qid="system-measure-panel"
               @hidden="reset">
    <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>

    <div v-if="!link" class="v2-field">
      <span class="v2-field__label">{{ $t('system_security.measure') }} <span class="v2-field__req">*</span></span>
      <b-form-input v-model="keyword" type="search" :placeholder="$t('system_security.measure_search')" qid="system-measure-search" @input="search"></b-form-input>
      <div class="sx-options">
        <button v-for="measure in measures"
                :key="measure.id"
                type="button"
                class="sx-option"
                :class="{ on: selected && selected.id === measure.id }"
                :disabled="linkedIds.includes(measure.id)"
                :qid="'system-measure-option-' + measure.id"
                @click="pick(measure)">
          <font-awesome-icon icon="shield-alt"/>
          <span class="sx-option__grow">
            {{ measure.name }}
            <small>{{ measureMeta(measure) }}</small>
          </span>
          <font-awesome-icon v-if="selected && selected.id === measure.id" icon="check"/>
        </button>
        <p v-if="loaded && !measures.length" class="sx-options__empty">{{ $t('system_security.no_catalogue') }}</p>
      </div>
    </div>

    <div v-if="current" class="v2-field">
      <span class="v2-field__label">{{ $t('system_security.in_place_question') }}</span>
      <p v-if="current.description" class="v2-field__note mt-0 mb-2">{{ current.description }}</p>
      <div class="sx-toggles">
        <button v-for="option in options"
                :key="option"
                type="button"
                :class="{ on: answers.includes(option) }"
                :aria-pressed="answers.includes(option) ? 'true' : 'false'"
                :qid="'system-measure-answer-' + option"
                @click="toggle(option)">
          <font-awesome-icon v-if="answers.includes(option)" icon="check"/>{{ optionLabel(option) }}
        </button>
      </div>
      <small class="v2-field__note">{{ $t('system_security.answer_hint') }}</small>
    </div>

    <template #foot>
      <span class="v2-panel__grow"></span>
      <b-button variant="secondary" @click="$refs.panel.hide()">{{ $t('systems.create.cancel') }}</b-button>
      <b-button variant="success" :disabled="saving || !current" qid="system-measure-submit" @click="save">
        <font-awesome-icon :icon="saving ? 'spinner' : 'check'" :class="{ 'fa-spin': saving }"/>
        {{ link ? $t('system_hero.panel.save') : $t('system_security.add_measure') }}
      </b-button>
    </template>
  </V2SidePanel>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import V2SidePanel from '@/components/V2SidePanel.vue';

  export default {
    name: 'SystemMeasurePanel',
    components: {
      V2SidePanel
    },
    props: {
      system: { type: Object, required: true },
      // Measures already on the system, from the security tab
      linked: { type: Array, default: () => [] }
    },
    data: function() {
      return {
        link: null,
        keyword: '',
        measures: [],
        loaded: false,
        selected: null,
        answers: [],
        saving: false,
        error: '',
        searchTimer: null
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      linkedIds() {
        return this.linked.map(measure => measure.id);
      },
      current() {
        return this.link || this.selected;
      },
      // Select measures have their own answers; the others are answered Yes or No
      options() {
        if (this.link) {
          return this.link.options;
        }
        if (this.selected.type === 'Select') {
          try {
            return JSON.parse(this.selected.type_options) || [];
          } catch (e) {
            return [];
          }
        }
        return ['Yes', 'No'];
      },
      multiple() {
        return this.current.type === 'Select';
      }
    },
    methods: {
      show(link) {
        this.link = link || null;
        if (link) {
          this.answers = (link.response || '').split(',').map(value => value.trim()).filter(Boolean);
        } else {
          this.loadMeasures();
        }
        this.$refs.panel.show();
      },
      reset() {
        Object.assign(this, { link: null, keyword: '', selected: null, answers: [], error: '' });
      },
      measureMeta(measure) {
        let parts = [];
        if (measure.measure_category && measure.measure_category.name) {
          parts.push(measure.measure_category.name);
        }
        if (this.linkedIds.includes(measure.id)) {
          parts.push(this.$t('system_link.already'));
        }
        return parts.join(' · ');
      },
      optionLabel(option) {
        if (option === 'Yes' || option === 'No') {
          return this.$t('system_security.answers.' + option.toLowerCase());
        }
        return option;
      },
      search() {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(this.loadMeasures, 300);
      },
      loadMeasures() {
        let self = this;
        piincHttp.get('measures', { params: {
          client: this.system.client || this.getClient.slug,
          per_page: 50,
          scope: 'system',
          keyword: this.keyword || undefined
        } }).then(function(response) {
          self.measures = response.data.items;
          self.loaded = true;
        }, function() {});
      },
      pick(measure) {
        this.selected = measure;
        this.answers = [];
      },
      toggle(option) {
        if (this.answers.includes(option)) {
          this.answers = this.answers.filter(value => value !== option);
        } else {
          this.answers = this.multiple ? this.answers.concat(option) : [option];
        }
      },
      save() {
        let self = this;
        let response = this.answers.join(',');
        let request = this.link
          ? piincHttp.put('systems/' + this.system.slug + '/measures/' + this.link.link_id, { response: response })
          : piincHttp.post('systems/' + this.system.slug + '/measures', { measure_id: this.selected.id, response: response || undefined });

        this.saving = true;
        this.error = '';
        request.then(function() {
          self.saving = false;
          self.$toastr('success', self.link ? self.$t('systems_overview.saved') : self.$t('activities.measures.create.success'));
          self.$refs.panel.hide();
          self.$emit('saved');
        }, function(error) {
          self.saving = false;
          self.error = (error.data && error.data.message) || self.$t('system_hero.panel.error');
        });
      }
    }
  }
</script>
